import clsx from "clsx";
import Link from "next/link";
import React from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { GlobalNavItemType } from "@/types/common/library";
import { ICON_TYPE, Icon } from "./Icon";
import ShortcutKbd from "./ShortcutKbd";

/**
 * Pulsing notification dot for nav items
 */
const NavAlertDot: React.FC = () => (
  <span className="relative flex h-2.5 w-2.5">
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gailPrimary opacity-75" />
    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-gailPrimary" />
  </span>
);

const NAV_ICON_VIEW_BOX_OVERRIDES: Partial<Record<GlobalNavItemType["icon"], string>> = {
  "user-headset": "0 -32 448 544",
};

type GlobalNavigationItemProps = GlobalNavItemType & {
  onItemClick?: () => void;
  isMinimized?: boolean;
};

export const GlobalNavigationItem: React.FC<GlobalNavigationItemProps> = ({
  onItemClick,
  id,
  isActive,
  label,
  href,
  target,
  onClick,
  icon,
  dataAttributes,
  isMinimized = false,
  hasAlert = false,
  shortcutHint,
}) => {
  const iconViewBox = NAV_ICON_VIEW_BOX_OVERRIDES[icon];

  const iconElement = (
    <span className={clsx("inline-flex items-center justify-center", "min-w-[20px]", !isMinimized && "align-middle")}>
      <Icon type={ICON_TYPE.REGULAR} icon={icon} viewBox={iconViewBox} />
    </span>
  );

  const labelElement = (
    <span
      className="align-middle"
      style={
        {
          transition: "opacity var(--fade-duration), width var(--fade-duration), margin var(--fade-duration)",
          opacity: isMinimized ? 0 : 1,
          overflow: "hidden",
          whiteSpace: "nowrap",
          width: isMinimized ? 0 : "100%",
          marginLeft: isMinimized ? 0 : 8,
          display: "inline-block",
        } as React.CSSProperties
      }
    >
      {label}
    </span>
  );

  const itemClassName = clsx(
    "block",
    "rounded-lg",
    "transition-all",
    "py-1",
    "text-sm",
    "my-[1.5px]",
    "mx-4 px-1.5",
    {
      "hover:bg-200 focus:bg-gailPrimary/10": true,
      "font-medium text-gailPrimary bg-gailPrimary/10 hover:bg-gailPrimary/10": isActive,
      "text-foreground/75": !isActive,
    }
  );

  const itemStyle = {
    overflow: "hidden",
    whiteSpace: "nowrap",
  } as React.CSSProperties;

  const shortcutElement = shortcutHint && (
    <span
      aria-hidden={isMinimized ? true : undefined}
      className="flex shrink-0 items-center overflow-hidden"
      style={{
        transition: "opacity var(--fade-duration), max-width var(--fade-duration)",
        opacity: isMinimized ? 0 : 1,
        maxWidth: isMinimized ? 0 : "100%",
      }}
    >
      <ShortcutKbd shortcut={shortcutHint} />
    </span>
  );

  const content = (
    <span className="flex items-center">
      {isMinimized ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="relative">
              {iconElement}
              {hasAlert && (
                <span className="absolute -top-0.5 -right-0.5">
                  <NavAlertDot />
                </span>
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            <p>
              {label}
              {shortcutHint ? ` (${shortcutHint})` : ""}
            </p>
          </TooltipContent>
        </Tooltip>
      ) : (
        iconElement
      )}
      {labelElement}
      {shortcutElement}
      {hasAlert && !isMinimized && (
        <span className="ml-2">
          <NavAlertDot />
        </span>
      )}
    </span>
  );

  if (onClick) {
    // Native button, not the library Button, which is a centered pill and can't be a left-aligned nav row.
    return (
      <button
        id={id}
        type="button"
        onClick={() => {
          onClick();
          onItemClick?.();
        }}
        className={clsx(itemClassName, "w-[calc(100%-2rem)] text-left")}
        style={itemStyle}
        {...dataAttributes}
      >
        {content}
      </button>
    );
  }

  return (
    <Link
      id={id}
      href={href || "#"}
      onClick={onItemClick}
      className={itemClassName}
      style={itemStyle}
      target={target}
      {...dataAttributes}
    >
      {content}
    </Link>
  );
};
